import React, { useEffect, useState } from "react";
import DragDrop from "../../DragDrop";
import axiosInstance from "../../../api/axiosInstance";
import { Pencil, Trash, WarningCircle, CaretDown } from "@phosphor-icons/react";
import ImageLoader from "../../loader/ImageLoader";
import PageLoader from "../../loader/PageLoader";
import { uploadImageToServer } from "../../../api/uploadImage";

const Restaurant = () => {
    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        countryCode: "91", // Default country code
        manager: "",
        breakfast: { from: "",to: ""},
        lunch: { from: "", to: "" },
        dinner: { from: "", to: "" },
        photos: [],
    });
    const [errors, setErrors] = useState({});
    const [uploadedImages, setUploadedImages] = useState([]);
    const [removedImageKeys, setRemovedImageKeys] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [existingData, setExistingData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const hotelId = localStorage.getItem("hotelId");

    const splitPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return { countryCode: "91", contact: "" };

        const numberStr = phoneNumber.toString();

        if (numberStr.length <= 10) {
            return { countryCode: "91", contact: numberStr };
        }

        if (numberStr.length === 11) {
            return {
                countryCode: numberStr.substring(0, 1),
                contact: numberStr.substring(1),
            };
        }

        if (numberStr.length === 12) {
            return {
                countryCode: numberStr.substring(0, 2),
                contact: numberStr.substring(2),
            };
        }

        // For numbers longer than 12 digits, assume first 2-3 digits are country code
        return {
            countryCode: numberStr.substring(0, numberStr.length - 10),
            contact: numberStr.substring(numberStr.length - 10),
        };
    };

    useEffect(() => {
        fetchExistingData();
    }, [hotelId]);

    // Utility function to parse the timing string
    const parseTiming = (timing) => {
        if (!timing)
            return { from: "", to: "" };
        const [from, to] = timing.split("-");
        // const [fromTime, fromPeriod] = [from.slice(0, -2), from.slice(-2)];
        // const [toTime, toPeriod] = [to.slice(0, -2), to.slice(-2)];
        return {
            from: from,
            // fromPeriod: fromPeriod.toUpperCase(),
            to: to,
            // toPeriod: toPeriod.toUpperCase(),
        };
    };

    const fetchExistingData = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/restaurant`,
            );
            if (
                response.data &&
                response.data.id &&
                response.data.name &&
                response.data.manager
            ) {
                const { countryCode, contact } = splitPhoneNumber(
                    response.data?.contact,
                );
                const parsedData = {
                    ...response.data,
                    countryCode,
                    contact: contact,
                    breakfast: parseTiming(response.data.breakfast_timings),
                    lunch: parseTiming(response.data.lunch_timings),
                    dinner: parseTiming(response.data.dinner_timings),
                };
                const existingPhotos = response.data.photos || [];
                fetchExistingImages(existingPhotos);
                setExistingData(parsedData);
                setFormData(parsedData);
                setIsEditing(false);
            } 
            else {
                setExistingData(null);
                setIsEditing(true);
            }
        } catch (error) {
            console.error("Failed to fetch existing data:", error);
            setExistingData(null);
            setIsEditing(true);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchExistingImages = async (imageKeys = []) => {
        const imagePromises = imageKeys.map(async (key) => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/images/download-presigned-url?key=${key}`,
                );
                const imageUrl = response.data.url;
                return { key, url: imageUrl, loading: false, error: false };
            } catch (error) {
                console.error("Error fetching image URL:", error);
                return { key, url: "", loading: false, error: true };
            }
        });

        const images = await Promise.all(imagePromises);
        setUploadedImages(images);
    };
    
    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        // console.log("Files to upload:", fileArray);

        const placeholders = fileArray.map((file, index) => ({
            id: `temp_${Date.now()}_${index}`,
            isLoading: true,
            file: file,
        }));

        setUploadedImages((prevImages) => [...prevImages, ...placeholders]);

        const uploadPromises = placeholders.map(async (placeholder, index) => {
            try {
                // console.log(
                //     `Starting upload for file ${index}:`,
                //     placeholder.file.name,
                // );
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${placeholder.file.name}`,
                );
                const { key, url } = uploadUrlResponse.data;
                await uploadImageToServer(placeholder.file, url);
                // console.log(
                //     `Upload completed for file ${index}:`,
                //     placeholder.file.name,
                // );

                return {
                    id: placeholder.id,
                    key,
                    url: URL.createObjectURL(placeholder.file),
                    isLoading: false,
                };
            } catch (error) {
                console.error(`Error uploading image ${index}:`, error);
                return { ...placeholder, isLoading: false, error: true };
            }
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        setUploadedImages((prevImages) =>
            prevImages.map(
                (img) => uploadedFiles.find((u) => u.id === img.id) || img,
            ),
        );

        const validFiles = uploadedFiles.filter((file) => !file.error);
        setFormData((prevData) => ({
            ...prevData,
            photos: [...prevData.photos, ...validFiles.map((f) => f.key)],
        }));
        setErrors((prev) => ({ ...prev, photos: "" }));
    };

    const handleRemoveMediaFiles = (indexToRemove) => {
        setUploadedImages((prevImages) => {
            const newImages = prevImages.filter(
                (_, index) => index !== indexToRemove,
            );
            const removedImage = prevImages[indexToRemove];
            if (
                removedImage &&
                !removedImage.isLoading &&
                !removedImage.error
            ) {
                setRemovedImageKeys((prev) => [...prev, removedImage.key]);
                setFormData((prevData) => ({
                    ...prevData,
                    photos: prevData.photos.filter(
                        (key) => key !== removedImage.key,
                    ),
                }));
            }
            return newImages;
        });
    };

    // ... (keep other existing functions like fetchExistingImages, handleFileUpload, etc.)

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (
            name.includes("breakfast") ||
            name.includes("lunch") ||
            name.includes("dinner")
        ) {
            const [meal, timeType] = name.split("-");
            setFormData((prevData) => ({
                ...prevData,
                [meal]: { ...prevData[meal], [timeType]: value },
            }));
        } 
        else if (name === "contact") {
           
            const numericValue = value.replace(/\D/g, "").slice(0, 10);
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: numericValue,
            }));
           
        } 
        else if(name==="countryCode"){
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value
            }));

        }
        else {
            setFormData((prevData) => ({ ...prevData, [name]: value }));
        }
        // Remove the error when the user starts typing
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleWorkingTimeChange = (meal, field, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [meal]: { ...prevData[meal], [field]: value },
        }));
        // Remove the error when the user changes the time
        setErrors((prevErrors) => ({ ...prevErrors, [meal]: "" }));
    };

    const validateForm = () => {
        // console.log("validateForm is called");
        const newErrors = {};

        if (!formData.name.trim()) {
            // console.log("Restaurant name missing");
            newErrors.name = "Restaurant name is required";
        }
        if (!formData.contact) {
            // console.log("Contact missing");
            newErrors.contact = "Contact is required";
        }
        if (!formData.manager.trim()) {
            // console.log("Manager name missing");
            newErrors.manager = "Manager's name is required";
        }

        // if (uploadedImages.length === 0)
        //     newErrors.photos = "At least one image is required";

        ["breakfast", "lunch", "dinner"].forEach((meal) => {
            if (!formData[meal].from || !formData[meal].to) {
                // console.log(`${meal} timings missing`);
                newErrors[meal] = `${
                    meal.charAt(0).toUpperCase() + meal.slice(1)
                } times are required`;
            }
            //  else {
            //     const fromTime = convertTo24Hour(
            //         formData[meal].from,
            //         formData[meal].fromPeriod,
            //     );
            //     const toTime = convertTo24Hour(
            //         formData[meal].to,
            //         formData[meal].toPeriod,
            //     );

            //     if (fromTime >= toTime) {
            //         console.log(`${meal} time range is invalid`);
            //         newErrors[meal] = `Invalid ${meal} time range`;
            //     }
            // }
        });

        setErrors(newErrors);
        console.log("Errors object:", newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const convertTo24Hour = (time, period) => {
        const [hours, minutes] = time.split(":").map(Number);
        let adjustedHours = hours;

        if (period === "PM" && hours !== 12) {
            adjustedHours += 12;
        } else if (period === "AM" && hours === 12) {
            adjustedHours = 0;
        }

        return adjustedHours * 60 + minutes;
    };

    // Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = validateForm();
        // console.log("Form validation result:", isValid);
        // console.log("Current errors:", errors);
        if (isValid) {
            const restaurantData = {
                name: formData.name.trim(),
                // contact: parseInt(
                //     formData.countryCode + formData.displayContact,
                // )
                contact :  parseInt(formData.countryCode + formData.contact),
                manager: formData.manager.trim(),
                breakfast_timings: `${formData.breakfast.from}-${formData.breakfast.to}`,
                lunch_timings: `${formData.lunch.from}-${formData.lunch.to}`,
                dinner_timings: `${formData.dinner.from}-${formData.dinner.to}`,
                photos: formData.photos.filter(
                    (key) => !removedImageKeys.includes(key),
                ),
            };
            // console.log("Country Code : ",formData.countryCode);
            // console.log("Restuarant Data  : ", restaurantData);
            // console.log("Restaurant Photos", restaurantData.photos);

      


            try {
                if (existingData) {
                    await axiosInstance.patch(
                        `/api/v1/hotels/${hotelId}/restaurant-info`,
                        restaurantData,
                    );
                } else {
                    await axiosInstance.post(
                        `/api/v1/hotels/${hotelId}/restaurant-info`,
                        restaurantData,
                    );
                }
                // console.log("Restaurant saved successfully");
                fetchExistingData();
            } catch (error) {
                console.error("Failed to save restaurant:", error);
            }
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const trimImgName = (name) => {
        return name && name.length > 12
            ? name.substring(0, 12) + "..."
            : name || "";
    };

    const handleImageLoad = (index) => {
        // Update the state to reflect that the image has finished loading
        const updatedImages = [...uploadedImages];
        updatedImages[index].isLoading = false;
        setUploadedImages(updatedImages);
    };

    const handleImageError = (index) => {
        // Handle image load error
        const updatedImages = [...uploadedImages];
        updatedImages[index].error = true;
        updatedImages[index].isLoading = false;
        setUploadedImages(updatedImages);
    };

    // ... (keep other existing functions)

    if (isLoading) {
        return <PageLoader />;
    }

    // Format Contact To Show
    const formatContact = (contact) => {
        if (!contact) return ""; // Return empty string if no contact is available

        // Convert contact to a string and remove any non-digit characters
        const cleanNumber = contact.toString().replace(/\D/g, "");

        // If contact is more than 10 digits, assume the first part is the country code
        if (cleanNumber.length > 10) {
            const countryCode = cleanNumber.slice(0, cleanNumber.length - 10);
            const actualContact = cleanNumber.slice(-10);
            return `+${countryCode} - ${actualContact}`;
        }

        // If contact is 10 digits or less, return it as is
        return cleanNumber;
    };

    if (existingData && !isEditing) {
        return (
            <div className="bg-blue-100 rounded-lg border-[1.5px] p-3 flex items-end justify-between h-fit mx-4 w-[30%]">
                <div>
                    <p>{existingData?.name}</p>
                    <p>{existingData?.manager}</p>
                    <p>+{existingData?.countryCode} {formatContact(existingData.contact)}</p>
                </div>
                <div>
                    <button
                        className="border-[1.5px] p-1 rounded-lg"
                        onClick={handleEdit}
                    >
                        <Pencil />
                    </button>
                </div>
            </div>
        );
    }

    // Render Time Input
    const renderTimeInput = (meal) => (
        <div className="w-full flex gap-2 mt-1">
            
            
            {/* From Timing */}
            <div className="w-1/2 border-[1.5px] rounded-lg flex h-[40px] items-center">
                <p className="w-[25%] px-2 border-r-[1.5px] h-full flex items-center">
                    From
                </p>
                <p className="w-[75%] text-center px-2 border-r-[1.5px] h-full flex items-center">
                    {/* <input
                        type="text"
                        placeholder="HH:MM"
                        value={formData[meal].from}
                        onChange={(e) =>
                            handleWorkingTimeChange(
                                meal,
                                "from",
                                e.target.value,
                            )
                        }
                        className="w-full text-center outline-none"
                        maxLength={5}
                    /> */}
                    <input type="time" className="w-full text-center outline-none" 
                        onChange={(e) =>
                            handleWorkingTimeChange(
                                meal,
                                "from",
                                e.target.value,
                            )
                        }
                        value={formData[meal].from.trim()}

                    />
                </p>
                {/* <div className="flex items-center w-[30%] gap-2 h-full px-2 justify-center">
                    <select
                        value={formData[meal].fromPeriod}
                        onChange={(e) =>
                            handleWorkingTimeChange(
                                meal,
                                "fromPeriod",
                                e.target.value,
                            )
                        }
                        className="bg-transparent"
                    >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                    </select>
                </div> */}
            </div>

            {/* To Timing */}
            <div className="w-1/2 border-[1.5px] rounded-lg flex h-[40px] items-center">
                <p className="w-[25%] px-2 border-r-[1.5px] h-full flex items-center">
                    To
                </p>
                <p className="w-[75%] text-center px-2 border-r-[1.5px] h-full flex items-center">
                    {/* <input
                        type="text"
                        placeholder="HH:MM"
                        value={formData[meal].to}
                        onChange={(e) =>
                            handleWorkingTimeChange(meal, "to", e.target.value)
                        }
                        className="w-full text-center outline-none"
                        maxLength={5}
                    /> */}
                    <input type="time"  className="w-full text-center outline-none"
                        value={formData[meal].to.trim()}
                        onChange={(e) =>
                            handleWorkingTimeChange(meal, "to", e.target.value)
                        }
                    />
                </p>
                {/* <div className="flex items-center w-[30%] gap-2 h-full px-2">
                    <select
                        value={formData[meal].toPeriod}
                        onChange={(e) =>
                            handleWorkingTimeChange(
                                meal,
                                "toPeriod",
                                e.target.value,
                            )
                        }
                        className="bg-transparent"
                    >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                    </select>
                </div> */}
            </div>


        </div>
    );

    return (
        <form
            onSubmit={handleSubmit}
            className="w-[60%] px-3 flex flex-col gap-3"
        >
            {/* Submit Button */}
            <button
                className="bg-[#ff7378] p-3 w-full rounded-lg"
                type="submit"
            >
                {existingData ? "Update Restaurant" : "Save Restaurant"}
            </button>

            {/* Restaurant Name */}
            <div>
                <p>Restaurant Name</p>
                <input
                    type="text"
                    placeholder="Enter your restaurant name"
                    name="name"
                    className="w-[50%] p-2 border-[1.5px] rounded-lg mt-1"
                    value={formData.name}
                    onChange={handleChange}
                />
                {errors.name && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.name}
                    </p>
                )}
            </div>
            
            {/* Phone Number  */}
            <div>
                <p>Phone Number</p>
                <div className="border-[1.5px] rounded-md flex  overflow-hidden w-[50%]">
                    <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="p-1.5 outline-none"
                    >
                        <option value="91">India (+91)</option>
                        <option value="1">USA (+1)</option>
                        <option value="44">UK (+44)</option>
                        <option value="86">China (+86)</option>
                        <option value="81">Japan (+81)</option>
                        {/* Add more country codes as needed */}
                    </select>
                    <input
                        type="text"
                        placeholder="Enter your number"
                        name="contact"
                        className="w-full p-2 outline-none"
                        value={formData.contact}
                        onChange={handleChange}
                        maxLength={10}
                    />
                </div>
                {errors.contact && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.contact}
                    </p>
                )}
            </div>
           
           {/* Manager Name */}
            <div>
                <p>Manager's Name</p>
                <input
                    type="text"
                    placeholder="Enter your manager name"
                    name="manager"
                    className="w-[50%] p-2 border-[1.5px] rounded-lg mt-1"
                    value={formData.manager}
                    onChange={handleChange}
                />
                {errors.manager && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.manager}
                    </p>
                )}
            </div>
            
            {/* Break Fast Time */}
            <div className="w-full">
                <p>Breakfast Timing</p>
                {renderTimeInput("breakfast")}
                {errors.breakfast && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.breakfast}
                    </p>
                )}
            </div>

            {/* Lunch Time */}
            <div className="w-full">
                <p>Lunch Timing</p>
                {renderTimeInput("lunch")}
                {errors.lunch && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.lunch}
                    </p>
                )}
            </div>

            {/* Dinner Time */}
            <div className="w-full">
                <p>Dinner Timing</p>
                {renderTimeInput("dinner")}
                {errors.dinner && (
                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                        <WarningCircle size={14} />
                        {errors.dinner}
                    </p>
                )}
            </div>

            {/* ... (keep the existing photo upload section) */}
            <div className="w-full flex flex-col">
                <p>Photo</p>
                <DragDrop
                    onFilesUploaded={handleFileUpload}
                    uploadedImages={uploadedImages}
                    onRemoveMediaFile={handleRemoveMediaFiles}
                    acceptedFileTypes={{
                        "image/*": [".jpeg", ".png", ".jpg"],
                    }}
                    maxFiles={10}
                    dragDropText="Drag and drop restaurant images here"
                />
                {errors.photos && (
                    <span className="text-[#E47186] text-xs flex items-center gap-1">
                        <WarningCircle className="text-sm" />
                        {errors.photos}
                    </span>
                )}
                {uploadedImages.length > 0 && (
                    <div className="flex gap-2 w-full mt-2 h-full flex-nowrap overflow-x-auto">
                        {uploadedImages.map((photo, index) => (
                            <div
                                key={photo.id}
                                className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] flex-shrink-0 overflow-hidden"
                            >
                                {photo.isLoading ? (
                                    <ImageLoader />
                                ) : photo.error ? (
                                    <div className="w-full h-full flex items-center justify-center text-red-500">
                                        Upload failed
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={photo.url}
                                            alt={`Restaurant ${index + 1}`}
                                            className="w-full h-[110px] bg-cover"
                                            onLoad={() =>
                                                handleImageLoad(index)
                                            }
                                            onError={() =>
                                                handleImageError(index)
                                            }
                                        />
                                        <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                            <p>{trimImgName(photo.key)}</p>
                                            <button
                                                className="rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px] bg-white"
                                                onClick={() =>
                                                    handleRemoveMediaFiles(
                                                        index,
                                                    )
                                                }
                                                type="button"
                                            >
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </form>
    );
};

export default Restaurant;
